import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { useAppDispatch } from 'hooks/reduxHooks'

import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getBnblPackageViews, getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import { BnblSteps, getBnblPackageViewByNumberOfRooms } from 'components/OfferPage/BnblModal/utils'
import FlashHotelRateOption from 'components/OfferPage/FlashHotel/SameRoom/FlashHotelRateOption'
import FlashHotelOptionPriceDetailsBNBLVariant from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/FlashHotelOptionPriceDetailsBNBLVariant'

export const StyledModalContent = styled(ModalContent)`
  padding: 0;
  background-color: ${props => props.theme.palette.neutral.default.six};
`

export const RoomHeaderContainer = styled(Group)`
  padding: ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  offer: App.Offer
  isGift: boolean
  setStep: (step: BnblSteps) => void
  packageView: App.OfferPackageOptionView
  numberOfRooms: number
  baseBnblPackageViews: Array<App.OfferPackageOptionView>
  selectedPackageUniqueKey: string
  setSelectedPackageUniqueKey: (uniqueKey: string) => void
}

function BnblChangePackageStep({
  offer,
  isGift,
  setStep,
  packageView,
  numberOfRooms,
  baseBnblPackageViews,
  selectedPackageUniqueKey,
  setSelectedPackageUniqueKey,
}: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const { duration, roomType } = packageView.package

  const packageViews = useMemo(() => baseBnblPackageViews.filter(pkg => pkg.package.duration === duration && pkg.package.roomType?.id === roomType?.id), [baseBnblPackageViews, duration, roomType])

  const onSelectPackage = useCallback((newPackageUniqueKey: string) => {
    setSelectedPackageUniqueKey(newPackageUniqueKey)
    const newSelectedPackageView = baseBnblPackageViews.find(baseBnblPackageView => baseBnblPackageView.package.uniqueKey === newPackageUniqueKey)
    if (!newSelectedPackageView) return

    const newPackages = [{
      packageView: getBnblPackageViewByNumberOfRooms(newSelectedPackageView, numberOfRooms),
      numberOfRooms,
    }]

    dispatch(initialiseBnblOfferModal(newPackages, offer.id, isGift))
    setStep(isGift ? BnblSteps.GIFT : BnblSteps.HOME)
  }, [baseBnblPackageViews, dispatch, numberOfRooms, offer, setSelectedPackageUniqueKey, setStep, isGift])

  return (
    <StyledModalContent>
      <Group direction="vertical" gap={16}>
        <RoomHeaderContainer direction="vertical" gap={4}>
          <Heading variant="heading5">{roomType?.name}</Heading>
          {!!packageViews?.length && <Caption variant="large">{intl.formatMessage(
            {
              id: 'AyZaP6qkDn',
              defaultMessage: '{count, plural, one {# option} other {# options}} available',
              description: 'Number of package options available for a room type in BNBL modal',
            },
            { count: packageViews.length },
          )}
          </Caption>}
        </RoomHeaderContainer>
        {packageViews?.map((view, index) => <Pane key={view.package.uniqueKey}>
          <FlashHotelRateOption
            packageView={view}
            packagePosition={packageViews.length > 1 ? index + 1 : undefined}
          >
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
              <FlashHotelOptionPriceDetailsBNBLVariant packageView={view} />
              {view.package.uniqueKey === selectedPackageUniqueKey && (
                <BodyTextBlock variant="medium" startIcon={<LineCheckIcon />}>
                  {intl.formatMessage({
                    id: 'PmipOREEW/',
                    defaultMessage: 'Selected',
                    description: 'Label shown when a BNBL package option is currently selected',
                  })}
                </BodyTextBlock>
              )}
              {view.package.uniqueKey !== selectedPackageUniqueKey && (
                <TextButton kind="primary" onClick={() => onSelectPackage(view.package.uniqueKey)}>
                  {intl.formatMessage({
                    id: 'l4eAZPC6yT',
                    defaultMessage: 'Select package',
                    description: 'Button to select a different BNBL package option on the form',
                  })}
                </TextButton>
              )}
            </Group>
          </FlashHotelRateOption>
        </Pane>)}
      </Group>
    </StyledModalContent>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State, ownProps: Props) => {
  const bnblModalPackages = appState.offer.bnblModal[ownProps.offer.id]
  const { numberOfRooms } = bnblModalPackages[0]

  return {
    numberOfRooms,
    packageOptions: getDurationPackageOptions(state, appState),
    baseBnblPackageViews: getBnblPackageViews(state),
  }
}, BnblChangePackageStep)
